import React from 'react';
import { useFetcher } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Textarea } from '@oysterjs/ui/textarea';
import { ActionResult } from '@oysterjs/core/graphql/actions';

import { FileInput } from '~/components/file';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import {
  visibilityInputFromValue,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';

import { LibraryUploadInfoCallout } from './library-upload-info-callout';

export interface CustomFormCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** POST action URL, e.g. `/app/library/forms/new`. */
  action: string;
}

/**
 * Add-form-to-library flow. Submits a multipart upload through the router
 * fetcher so the parent loader revalidates after upload; the dialog closes
 * itself once the action returns idle with no payload error, otherwise it
 * surfaces the error message inline.
 */
export function CustomFormCreateDialog({ open, onClose, action }: CustomFormCreateDialogProps) {
  const fetcher = useFetcher();

  const [title, setTitle] = React.useState('');
  const [file, setFile] = React.useState<File | null>(null);
  const [ingestionGuidance, setIngestionGuidance] = React.useState('');
  const [error, setError] = React.useState<string>();
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const visibilityEnabled = useVisibilityEntitlement();
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(defaultVisibility);

  /** True between handleSubmit and the next idle that follows it. */
  const pendingSubmitRef = React.useRef(false);

  const inFlight = fetcher.state === 'submitting' || fetcher.state === 'loading';

  const actionPayload =
    fetcher.data && typeof fetcher.data === 'object'
      ? (fetcher.data as NonNullable<ActionResult<unknown>>)
      : null;
  const actionFailed =
    !!actionPayload &&
    (Boolean(actionPayload.applicationError) || (actionPayload.validationErrors?.length ?? 0) > 0);

  React.useEffect(() => {
    if (!open) {
      pendingSubmitRef.current = false;
      return;
    }
    setSubmitAttempted(false);
    setError(undefined);
    pendingSubmitRef.current = false;
  }, [open]);

  const resetFields = React.useCallback(() => {
    setTitle('');
    setFile(null);
    setIngestionGuidance('');
    setVisibility(defaultVisibility);
    setSubmitAttempted(false);
    setError(undefined);
  }, [defaultVisibility]);

  // Resolve a completed submission: surface errors or reset + close on success.
  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !pendingSubmitRef.current) return;
    pendingSubmitRef.current = false;
    if (actionFailed) {
      if (actionPayload?.applicationError) {
        setError(actionPayload.applicationError.message ?? 'Failed to upload form');
        return;
      }
      if (actionPayload?.validationErrors?.length) {
        setError(actionPayload.validationErrors.map((e) => e.message).join(' '));
        return;
      }
    }
    resetFields();
    onClose();
  }, [actionFailed, actionPayload, fetcher.state, onClose, resetFields]);

  // Reset on cancel/X so re-opening the dialog never resurrects abandoned
  // input. Matches `WordTemplateCreateDialog.handleClose` behaviour.
  const handleClose = React.useCallback(() => {
    if (inFlight) return;
    resetFields();
    onClose();
  }, [inFlight, onClose, resetFields]);

  const titleInvalid = submitAttempted && !title.trim();
  const fileInvalid = submitAttempted && !file;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || !file) {
      setSubmitAttempted(true);
      return;
    }
    setError(undefined);

    const formData = new FormData();
    formData.set('title', trimmedTitle);
    formData.set('file', file);
    if (ingestionGuidance.trim()) {
      formData.set('ingestionGuidance', ingestionGuidance.trim());
    }
    if (visibilityEnabled) {
      formData.set('visibility', JSON.stringify(visibilityInputFromValue(visibility)));
    }

    pendingSubmitRef.current = true;
    fetcher.submit(formData, { method: 'POST', action, encType: 'multipart/form-data' });
  };

  return (
    <Dialog open={open} onClose={handleClose} size="xl">
      <div className="min-w-0">
        <DialogTitle>Add form to library</DialogTitle>
        <DialogDescription>
          Cara reads the form's fields, including any text you enter and options you select, to
          understand how the form should be completed. Scanned and flattened PDFs work too: Cara
          finds their fields itself.
        </DialogDescription>
      </div>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <LibraryUploadInfoCallout title="Before you upload">
                <div>
                  <strong>If your PDF has no fillable fields:</strong> Upload it as-is. Cara detects
                  where answers belong and adds the fields automatically.
                </div>
                <div>
                  <strong>Text fields:</strong> Optionally enter a short instruction or example if
                  the field needs explanation. Cara uses this as guidance, not as a default answer.
                </div>
                <div>
                  <strong>Yes/no or choice fields:</strong> Select an option only if you want Cara
                  to use it as the default when your documents do not specify an answer.
                </div>
                <div>
                  <strong>No guidance or default needed:</strong> Leave the field blank.
                </div>
                <div>
                  <strong>Remove a field:</strong> Type [[remove]] as the field&apos;s entire value.
                  If Cara added an unwanted field to a scanned or flattened PDF, download the
                  prepared PDF after processing, mark the field this way, and upload it again.
                </div>
                <div>
                  <strong>Form size limits:</strong> Best results are with forms that have fewer
                  than 400 fields and 8 pages or less. Larger forms may not work well.
                </div>
              </LibraryUploadInfoCallout>

              <Field>
                <Label>Form title</Label>
                <Input
                  invalid={titleInvalid}
                  value={title}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setTitle(v);
                    if (v.trim() && file) setSubmitAttempted(false);
                  }}
                  placeholder="e.g. Hartford GL Supplemental"
                />
                {titleInvalid && <ErrorMessage>Enter a form title.</ErrorMessage>}
              </Field>

              <Field>
                <Label>PDF file</Label>
                <Description>
                  The form you want to save. When the PDF has fillable fields, Cara reads entered
                  text as guidance and selected options as optional defaults.
                </Description>
                <FileInput
                  invalid={fileInvalid}
                  onChange={(change) => {
                    const next = change.files[0]?.upload ?? null;
                    setFile(next);
                    if (next && title.trim()) setSubmitAttempted(false);
                  }}
                />
                {fileInvalid && <ErrorMessage>Choose a PDF file.</ErrorMessage>}
              </Field>

              <Field>
                <Label>Setup guidance (optional)</Label>
                <Description>
                  Use this for extra context that is not obvious from the PDF itself. For example,
                  whether a checkbox is account-wide or location-specific, or how to interpret
                  rates, percentages, and limits.
                </Description>
                <Textarea
                  value={ingestionGuidance}
                  onChange={(e) => setIngestionGuidance(e.currentTarget.value)}
                  rows={4}
                  placeholder="Example: For payroll and sales fields, use annual totals."
                />
              </Field>

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={handleClose} disabled={inFlight}>
            Cancel
          </Button>
          {visibilityEnabled ? (
            <VisibilitySplitButton
              type="submit"
              value={visibility}
              onChange={setVisibility}
              loading={inFlight}
            >
              Add form
            </VisibilitySplitButton>
          ) : (
            <Button type="submit" color="sky" loading={inFlight}>
              Add form
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
